'use client';
import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import Smartlook from 'smartlook-client';

const gtmId = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-TJ7D8F6';

export function AnalyticsScripts() {
  if (
    process.env.NEXT_PUBLIC_NO_ANALYTICS ||
    process.env.VERCEL_ENV !== 'production'
  ) {
    return <LogAnalyticsScripts />;
  } else {
    return <RunAnalyticsScripts />;
  }
}

function RunAnalyticsScripts() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    Smartlook.init(
      'ae873f0d87381eb4a59bfbdc267263bc94c41184',
      {
        region: 'eu'
      },
      () => {
        Smartlook.record({ forms: true, numbers: true, emails: true });
      }
    );
  }, []);

  useEffect(() => {
    // Clear last page view the first time this component is loaded
    // e.g. on refresh or crash
    window.sessionStorage.setItem('lastPageView', null);
  }, []);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    // Ignore these events, since they'll be handled by the "fake" form pages
    if (
      !/^\/applications\//.test(currentUrl.pathname) &&
      !/^\/finalize\//.test(currentUrl.pathname) &&
      !/^\/co-applicant\//.test(currentUrl.pathname)
    ) {
      const event = new Event('pageview');
      event.override = currentUrl.toString();
      window.dispatchEvent(event);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    const pageViewEventHandler = (e) => {
      const currentUrl = new URL(e.override || window.location.href);
      if (e.form && e.step && typeof e.index !== 'undefined') {
        currentUrl.pathname = `/form-step/${e.form}/${e.index}_${e.step}`;
      } else if (e.form && e.step) {
        currentUrl.pathname = `/form-step/${e.form}/${e.step}`;
      }
      const currentUrlStr = currentUrl.toString();
      const lastPageView = window.sessionStorage.getItem('lastPageView');
      if (currentUrlStr !== lastPageView) {
        window.sessionStorage.setItem('lastPageView', currentUrlStr);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'pageview',
          page_location: currentUrlStr,
          page_path: currentUrl.pathname,
          page_title: document.title,
          page_referrer: lastPageView || document.referrer
        });
      }
    };
    const formFinishEventHandler = (e) => {
      if (e.form === 'application') {
        const formData = e.data;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'applicationSubmissionDetected',
          DoctorName:
            formData?.doctor?.value === 'other'
              ? formData?.doctor?.other_doctor
              : formData?.doctor?.label
        });
      }
    };
    window.addEventListener('formpagechange', pageViewEventHandler);
    window.addEventListener('pageview', pageViewEventHandler);
    window.addEventListener('formfinish', formFinishEventHandler);
    return () => {
      window.removeEventListener('formpagechange', pageViewEventHandler);
      window.removeEventListener('pageview', pageViewEventHandler);
      window.removeEventListener('formfinish', formFinishEventHandler);
    };
  }, []);

  return (
    <>
      <Script id="gtm_script" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer', '${gtmId}');
        `}
      </Script>
      <Script id="bing_script" strategy="afterInteractive">
        {`
          (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){
          var o={ti:"136024619"};o.q=w[u],w[u]=new UET(o),
          w[u].push("pageLoad")},n=d.createElement(t),
          n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){
          var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},
          i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
        `}
      </Script>
      <Script
        id="igo_script"
        src="//534000918.collect.igodigital.com/collect.js"
        strategy="afterInteractive"
      />
      <Script id="meta_pixel_script" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '2172354716266004');
          fbq('track', 'PageView');
        `}
      </Script>
    </>
  );
}

function LogAnalyticsScripts() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Clear last page view the first time this component is loaded
    // e.g. on refresh or crash
    window.sessionStorage.setItem('lastPageView', null);
  }, []);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    // Ignore these events, since they'll be handled by the "fake" form pages
    if (
      !/^\/applications\//.test(currentUrl.pathname) &&
      !/^\/finalize\//.test(currentUrl.pathname) &&
      !/^\/co-applicant\//.test(currentUrl.pathname)
    ) {
      const event = new Event('pageview');
      event.override = currentUrl.toString();
      window.dispatchEvent(event);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    const pageViewEventHandler = (e) => {
      const currentUrl = new URL(e.override || window.location.href);
      if (e.form && e.step && typeof e.index !== 'undefined') {
        currentUrl.pathname = `/form-step/${e.form}/${e.index}_${e.step}`;
      } else if (e.form && e.step) {
        currentUrl.pathname = `/form-step/${e.form}/${e.step}`;
      }
      const currentUrlStr = currentUrl.toString();
      const lastPageView = window.sessionStorage.getItem('lastPageView');
      if (currentUrlStr !== lastPageView) {
        console.log('PAGEVIEW EVENT', {
          event: 'pageview',
          page_location: currentUrlStr,
          page_path: currentUrl.pathname,
          page_title: document.title,
          page_referrer: lastPageView || document.referrer
        });
        window.sessionStorage.setItem('lastPageView', currentUrlStr);
      }
    };
    const formFinishEventHandler = (e) => {
      const formData = e.data;
      if (e.form === 'application') {
        console.log('FORMFINISH EVENT', {
          event: 'applicationSubmissionDetected',
          formName: e.form,
          DoctorName:
            formData?.doctor?.value === 'other'
              ? formData?.doctor?.other_doctor
              : formData?.doctor?.label
        });
      } else {
        console.log('UNSENT FORMFINISH EVENT', {
          event: 'applicationSubmissionDetected',
          formName: e.form,
          DoctorName:
            formData?.doctor?.value === 'other'
              ? formData?.doctor?.other_doctor
              : formData?.doctor?.label
        });
      }
    };
    window.addEventListener('formpagechange', pageViewEventHandler);
    window.addEventListener('pageview', pageViewEventHandler);
    window.addEventListener('formfinish', formFinishEventHandler);
    return () => {
      window.removeEventListener('formpagechange', pageViewEventHandler);
      window.removeEventListener('pageview', pageViewEventHandler);
      window.removeEventListener('formfinish', formFinishEventHandler);
    };
  }, []);
}
